import type { CompleteDeviceVersionObject } from "../schemas/commonSubSchemas/completeDeviceVersion.js";
import { DeviceVersion } from "../schemas/commonSubSchemas/deviceVersion.js";
import {
    assertCompleteDeviceVersionIsWellFormed,
    COMPLETE_DEVICE_VERSION_SEGMENTS,
    ITEM_CHARACTER_PATTERN,
    ITEM_PATTERNS,
    type SegmentKind,
    termPattern,
    UNKNOWN_ITEM_PATTERN,
    WILDCARD_ITEM_PATTERN,
} from "./completeDeviceVersionGrammar.js";
import { regexForNumberGreaterThanOrEqualTo, regexForNumberLessThanOrEqualTo } from "./regexUtils.js";
import { deviceVersionStringToObject } from "./VerdiDeviceVersionUtilities.js";

/**
 * Regex pattern generators for version selector strings.
 * Used where only regex-based validation is available (e.g. Zod schema constraints).
 *
 * For runtime version matching, use `doesDeviceVersionMatchSelector` in VerdiDeviceVersionUtilities.
 *
 * `doesDeviceVersionMatchVersionSelectorBasedOnRegex` is exported for **test infrastructure only**:
 * it exercises the regex factory against the canonical matcher so we can catch regressions in the
 * generated patterns. Do not use it in production code.
 *
 * For complete device versions, `doesCompleteDeviceVersionMatchSelector` is the canonical matcher; the complete-version
 * generators below are cross-checked against it in `completeDeviceVersionMatching.parity.test.ts`.
 *
 * Note: The regex-based matcher cannot detect malformed version selectors. If the version selector
 * is malformed, the output is considered undefined.
 */

/**
 * Type guard to check if a version parameter is a string.
 *
 * @param version - A DeviceVersion object or version string
 * @returns True if version is a string, with type narrowing to string
 */
function isDeviceVersionString(version: DeviceVersion | string): version is string {
    return typeof version === "string" || version instanceof String;
}
/**
 * Generates a regex pattern that matches a single numeric version selector.
 * Supports wildcards (*), exact matches, ranges (min:max), greater-than (min+), and less-than (max-).
 *
 * @param number - The numeric value to match against
 * @returns A regex pattern string matching various numeric selector formats
 *
 * @example
 * // Matches: "4", "*", "3+", "5-", "2:6"
 * getRegexToMatchSingleNumericSelector(4)
 */
function getRegexToMatchSingleNumericSelector(number: number): string {
    const options = [
        "(?:\\*)",
        `(?:${number})`,
        `(?:${regexForNumberLessThanOrEqualTo(number)}\\+)`,
        `(?:${regexForNumberGreaterThanOrEqualTo(number)}\\-)`,
        `(?:${regexForNumberLessThanOrEqualTo(number)}\\:${regexForNumberGreaterThanOrEqualTo(number)})`,
    ];
    return `(?:${options.join("|")})`;
}

/**
 * Generates a regex pattern that matches a numeric term in a version selector.
 * Handles both single values and comma-separated lists in brackets.
 *
 * @param number - The numeric value to match against
 * @returns A regex pattern string matching numeric terms like "[2,4,6]" or "3+" or "5"
 *
 * @example
 * // Matches: "4", "[2,4,6]", "[1:5, 8+]"
 * getRegexToMatchSingleNumericTerm(4)
 */
function getRegexToMatchSingleNumericTerm(number: number): string {
    return `(\\[*([\\d*\\-\\+\\: ]*,)* *${getRegexToMatchSingleNumericSelector(number)} *,* *([\\d*\\-\\+\\: ]*,*)*\\]*)`;
}

/**
 * Generates a regex pattern that matches a non-numeric term (like product line or hardware variant).
 * Handles both single values and comma-separated lists in brackets.
 *
 * @param name - The string value to match against
 * @returns A regex pattern string matching terms like "[mega,micro]" or "mega" or "*"
 *
 * @example
 * // Matches: "mega", "[mega,micro]", "*"
 * getRegexToMatchSingleNonNumericTerm("mega")
 */
function getRegexToMatchSingleNonNumericTerm(name: string): string {
    return `(\\[*([a-zA-Z0-9, ]*,)* *(?:${name}|\\*) *,* *([a-zA-Z0-9, ]*,*)*\\]*)`;
}

/**
 * Generates a regex pattern that matches a complete single version selector from a DeviceVersion object.
 * Combines all version components (generation, productLine, hardwareVersion, hardwareVariant, firmwareVersion).
 *
 * @param version - The DeviceVersion object to generate a matcher for
 * @returns A regex pattern string matching the complete version selector
 *
 * @example
 * // For version {generation: 3, productLine: "mega", hardwareVersion: 2, hardwareVariant: "a", firmwareVersion: 5}
 * // Matches: "3.mega.2.a.5", "3.[mega,micro].2+.a.5", etc.
 * getRegexToMatchSingleVersionSelectorFromVersion(deviceVersion)
 */
function getRegexToMatchSingleVersionSelectorFromVersion(version: DeviceVersion): string {
    const terms = [
        getRegexToMatchSingleNumericTerm(version.generation),
        getRegexToMatchSingleNonNumericTerm(version.productLine),
        getRegexToMatchSingleNumericTerm(version.hardwareVersion),
        getRegexToMatchSingleNonNumericTerm(version.hardwareVariant),
        getRegexToMatchSingleNumericTerm(version.firmwareVersion),
    ];
    return `(?:${terms.join("\\.")})`;
}

/**
 * Generates a regex pattern that matches a version selector (with or without parentheses).
 * This is used to match positive version constraints in a selector string.
 *
 * Careful! You must use both the positive and negative selectors for this to work properly.
 * And negative selectors must override positive ones. Otherwise your results will be wrong. Example
 * implementations are below this function
 *
 * @param version - The DeviceVersion object to generate a matcher for
 * @returns A regex pattern string matching version selectors like "3.mega.2.a.5" or "(3.mega.2.a.5)"
 *
 *
 *
 * @example
 * // Matches: "3.mega.2.a.5" or "(3.mega.2.a.5)"
 * getRegexToMatchVersionSelectorFromVersion(deviceVersion)
 */
export function getRegexToMatchPositiveVersionSelectorFromVersion(version: DeviceVersion): string {
    return `(?: *${getRegexToMatchSingleVersionSelectorFromVersion(version)} *)|(?: *\\( *${getRegexToMatchSingleVersionSelectorFromVersion(version)} *\\) *)`;
}

/**
 * Generates a regex pattern that matches a negative version selector (exclusion constraint).
 * This is used to match "!(version)" patterns that exclude specific versions.
 *
 * Careful! You must use both the positive and negative selectors for this to work properly.
 * And negative selectors must override positive ones. Otherwise your results will be wrong. Example
 * implementations are below this function
 *
 * @param version - The DeviceVersion object to generate a matcher for
 * @returns A regex pattern string matching negative selectors like "!(3.mega.2.a.5)"
 *
 * @example
 * // Matches: "!(3.mega.2.a.5)"
 * getRegexToMatchNegativeVersionSelectorFromVersion(deviceVersion)
 */
export function getRegexToMatchNegativeVersionSelectorFromVersion(version: DeviceVersion): string {
    return `\\!\\( *${getRegexToMatchSingleVersionSelectorFromVersion(version)} *\\)`;
}

/** Numbers with the same digit count as `number` that are below or above it. */
function sameLengthNumberAlternatives(number: number, direction: "below" | "above"): string[] {
    const digits = String(number);
    return Array.from(digits).flatMap((digit, i) => {
        const value = Number(digit);
        const [from, to] = direction === "below" ? [0, value - 1] : [value + 1, 9];
        if (from > to) {
            return [];
        }
        const remaining = digits.length - i - 1;
        return [`${digits.slice(0, i)}[${from}-${to}]${remaining > 0 ? `[0-9]{${remaining}}` : ""}`];
    });
}

/** Numbers less than or equal to `number`, allowing leading zeros. */
function numbersAtMostPattern(number: number): string {
    const digits = String(number).length;
    const shorter = digits > 1 ? [`[0-9]{1,${digits - 1}}`] : [];
    return `0*(?:${[...shorter, ...sameLengthNumberAlternatives(number, "below"), String(number)].join("|")})`;
}

/** Numbers greater than or equal to `number`, allowing leading zeros. */
function numbersAtLeastPattern(number: number): string {
    const digits = String(number).length;
    const longer = `[1-9][0-9]{${digits},}`;
    return `0*(?:${[String(number), ...sameLengthNumberAlternatives(number, "above"), longer].join("|")})`;
}

/** Numeric item matching a known value: "x", "x+", "x-" or "x:y". */
function numericItemPattern(number: number): string {
    // x+ matches when x <= value, so its bound is a number at most value; x- is the mirror image.
    const lowerBound = numbersAtMostPattern(number);
    const upperBound = numbersAtLeastPattern(number);
    return `(?:0*${number}|${lowerBound}\\+|${upperBound}\\-|${lowerBound}:${upperBound})`;
}

/** Items matching one known value. */
const itemsMatchingValuePattern: Record<SegmentKind, (value: string | number) => string> = {
    numeric: (value) => numericItemPattern(Number(value)),
    text: String,
};

/**
 * A term (bare item or bracketed list) in which some item matches the segment value. The lookahead finds the matching
 * item and the rest consumes the term in one way only.
 */
function segmentTermPattern(kind: SegmentKind, value: string | number | undefined): string {
    const anyItem = ITEM_PATTERNS[kind];
    const matchingItem = `(?:${WILDCARD_ITEM_PATTERN}|${value === undefined ? UNKNOWN_ITEM_PATTERN : itemsMatchingValuePattern[kind](value)})`;
    // (?!ITEM_CHARACTER) makes matches whole items: 12 does not match 123, block does not match blockx.
    return `(?=(?:\\[ *(?:${anyItem} *, *)*)?${matchingItem}(?!${ITEM_CHARACTER_PATTERN}))${termPattern(anyItem)}`;
}

/** Single complete-version selector regex. Throws unless the version is well-formed. */
function singleCompleteSelectorPattern(version: CompleteDeviceVersionObject): string {
    assertCompleteDeviceVersionIsWellFormed(version);
    const terms = COMPLETE_DEVICE_VERSION_SEGMENTS.map(({ key, kind }) => segmentTermPattern(kind, version[key]));
    return `(?:${terms.join("\\.")})`;
}

/**
 * Matches a selector that includes the version: a bare selector matching it, or a "(...)" inclusion constraint matching
 * it. Use with the negative regex, whose matches override this one.
 */
export function getRegexToMatchPositiveVersionSelectorFromCompleteDeviceVersion(
    version: CompleteDeviceVersionObject,
): string {
    const singleSelector = singleCompleteSelectorPattern(version);
    // (?![\s\S]) instead of $: PCRE's $ also matches before a trailing newline.
    // (?:^|\)) only rejects junk before "("; exclusions are handled by the negative regex.
    return `(?:^ *${singleSelector} *(?![\\s\\S]))|(?:(?:^|\\)) *\\( *${singleSelector} *\\))`;
}

/** Matches a "!(...)" exclusion constraint matching the version. */
export function getRegexToMatchNegativeVersionSelectorFromCompleteDeviceVersion(
    version: CompleteDeviceVersionObject,
): string {
    return `\\! *\\( *${singleCompleteSelectorPattern(version)} *\\)`;
}

/**
 * **Test infrastructure only.** Do not call from production code.
 *
 * Exercises the regex pattern generators above by matching a device version against a selector
 * string the same way the Zod schema constraints would. Cross-tested in
 * `verdiDeviceVersionMatching.test.ts` against `doesDeviceVersionMatchSelector` to ensure the
 * regex factory stays robust.
 *
 * @param version - A DeviceVersion object or version string to test
 * @param versionSelector - The version selector string to test against (e.g., "(3.mega.2+.a.5) !(3.mega.2.a.4)")
 * @returns True if the version matches the selector (included and not excluded), false otherwise
 *
 * @example
 * // Returns true if version "3.mega.2.a.5" matches selector "(3.mega.2+.a.5)"
 * doesDeviceVersionMatchVersionSelectorBasedOnRegex("3.mega.2.a.5", "(3.mega.2+.a.5)")
 */
export function doesDeviceVersionMatchVersionSelectorBasedOnRegex(
    version: DeviceVersion | string,
    versionSelector: string,
): boolean {
    const deviceVersion: DeviceVersion = isDeviceVersionString(version)
        ? deviceVersionStringToObject(version)
        : version;

    return (
        new RegExp(`${getRegexToMatchPositiveVersionSelectorFromVersion(deviceVersion)}`, "i").test(versionSelector) &&
        !new RegExp(`${getRegexToMatchNegativeVersionSelectorFromVersion(deviceVersion)}`, "i").test(versionSelector)
    );
}
